import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, User, Eye, EyeOff, ArrowRight, Calendar, Users } from "lucide-react";
import { toast } from "sonner";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { isWebPlatform } from "@/lib/platform";
import SportsBioIcon from "@/components/SportsBioIcon";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { computeAge, MIN_PLAYER_AGE, MINOR_AGE_CEILING } from "@/lib/age";
import { canAttemptSignup, recordSignupAttempt } from "@/lib/rateLimit";
import { sendParentalConsentEmail } from "@/lib/parentalConsent";
import { reportError } from "@/lib/sentry";
import { getErrorMessage } from "@/lib/errors";
import { track, ageToBucket, analytics } from "@/lib/analytics";
import { SCOUT_SIGNUPS_ENABLED } from "@/lib/featureFlags";
import { ADMIN_HOME_PATH, adminReturnPath, isAdminUserId } from "@/lib/adminAccess";
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';

// Custom-scheme deep link Supabase redirects to after Google OAuth on
// native. Matches the AndroidManifest intent filter declared in
// android/app/src/main/AndroidManifest.xml. The host
// (`auth-callback`) is also enforced in src/hooks/useOAuthDeepLink.ts.
const NATIVE_OAUTH_REDIRECT_URL = "com.sportsbio.app://auth-callback";

// ========================================================================
// TURNSTILE CAPTCHA — DISABLED FOR v1.0
//
// Turnstile was disabled due to connectivity issues during pre-launch
// testing. All Turnstile code is preserved (component imports, widget
// render, server-side verify-captcha Edge Function, env vars) so the
// flow can be reactivated by flipping ONE constant.
//
// To re-enable for v1.1:
//   1. Set TURNSTILE_ENABLED = true below.
//   2. Verify VITE_TURNSTILE_SITE_KEY is set in the production env.
//   3. Confirm supabase/functions/verify-captcha is still deployed
//      (`supabase functions list`) and TURNSTILE_SECRET_KEY is set
//      (`supabase secrets list`).
//   4. Confirm the Cloudflare Dashboard site key still allowlists the
//      production domain.
//   5. (Belt-and-braces) Confirm Supabase Dashboard → Authentication
//      → Auth Settings does NOT have built-in "Enable CAPTCHA
//      protection" toggled — our flow uses the custom verify-captcha
//      EF and supabase.auth.signUp() does not pass captchaToken. If
//      the dashboard toggle is on, it'd reject signups regardless of
//      whether the widget is shown client-side.
//   6. Test signup end-to-end on both Player and Scout personas.
//
// Anti-bot protection while Turnstile is OFF rests on:
//   • Supabase Auth rate limiting (60 signups/hour per IP by default)
//   • Email verification required before account is functional
//   • Resend rate limits on transactional emails
//   • Sentry monitoring for unusual signup spikes
//
// Risk: higher signup-spam potential. Monitor for unusual signup
// velocity in PostHog (user_signed_up event); if it spikes, re-enable.
// ========================================================================
const TURNSTILE_ENABLED = false;

// Cloudflare Turnstile site key. Public — safe to ship in the client
// bundle (it's the *secret* key that lives in Supabase secrets). When this
// env var is unset (dev environment without Turnstile configured), the
// widget is skipped and signup proceeds without CAPTCHA verification.
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

/**
 * POSTs the widget's response token to verify-captcha. Returns the
 * Edge Function's verdict in a shape the call site can branch on.
 * Never throws — failures (network, function error, validation 4xx) are
 * folded into { ok: false, error }. Cloudflare tokens are single-use, so
 * call sites must reset the widget after any failure here.
 */
async function verifyCaptchaToken(token: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke("verify-captcha", {
      body: { token },
    });
    if (error) return { ok: false, error: error.message };
    if (data && typeof data === "object" && (data as { success?: unknown }).success === true) {
      return { ok: true };
    }
    const msg =
      data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : "Verification failed.";
    return { ok: false, error: msg };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : "Verification failed." };
  }
}

type PersonaValue = "player" | "scout" | "academy";

// Personas offered on the SIGNUP toggle. Scout is dropped while its
// registration is frozen, so there is no way to start a scout signup from
// here. The toggle is signup-only (`!isLogin` below), so existing scouts
// are unaffected — they log in and land on their own surface as before.
const PERSONA_OPTIONS: ReadonlyArray<{ value: PersonaValue; label: string }> = [
  { value: "player", label: "Player" },
  ...(SCOUT_SIGNUPS_ENABLED
    ? [{ value: "scout" as PersonaValue, label: "Scout" }]
    : []),
  { value: "academy", label: "Academy" },
];

interface PendingSignupData {
  persona: PersonaValue;
  // Player-only fields. Stashed in localStorage so we can apply them after
  // a Supabase email-confirmation redirect (which leaves the page before
  // the profile row is finalised).
  dob?: string;
  parentEmail?: string;
  parentName?: string;
  consentToken?: string;
}

// Stash the persona the user picks on the signup screen so we can apply it
// after a Supabase OAuth redirect or a magic-link email confirmation — both
// of which leave the page before we can write the role to the profile row.
const PENDING_SIGNUP_KEY = "sportsbio_pending_signup";

const persistPendingSignup = (data: PendingSignupData) => {
  try { localStorage.setItem(PENDING_SIGNUP_KEY, JSON.stringify(data)); } catch { /* ignore */ }
};

const consumePendingSignup = (): PendingSignupData | null => {
  try {
    const raw = localStorage.getItem(PENDING_SIGNUP_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingSignupData;
    // A pending scout signup stored before the freeze (or hand-crafted in
    // localStorage) must not resume — drop it rather than apply the role.
    if (parsed.persona === "scout" && !SCOUT_SIGNUPS_ENABLED) {
      localStorage.removeItem(PENDING_SIGNUP_KEY);
      return null;
    }
    if (parsed.persona === "player" || parsed.persona === "scout" || parsed.persona === "academy") {
      localStorage.removeItem(PENDING_SIGNUP_KEY);
      return parsed;
    }
  } catch { /* ignore */ }
  return null;
};

const onboardingPathFor = (persona: PersonaValue): string =>
  persona === "academy" ? "/academy/onboarding"
  : persona === "scout" ? "/scout/onboarding"
  : "/player/onboarding";

// MODULE-LEVEL dedupe (intentionally not a React ref). A guard further
// down the route tree (PlatformGuard at /player/* and /scout/* on the
// web build, or RequireAuth's wrong-role check) can bounce the user
// back to /auth, which UNMOUNTS and RE-MOUNTS AuthScreen — wiping any
// per-component ref. Without a module-level cache, the redirect effect
// resolves the same user → same gated destination → bounce → remount,
// in a loop until Supabase rate-limits the auth calls (~50 iters).
//
// Cleared whenever the AuthScreen sees `user` flip to null
// (post-signOut), so a real account switch re-resolves cleanly.
let lastResolvedDestForUserId: string | null = null;
let lastResolvedDest: string | null = null;

const homePathFor = (persona: PersonaValue): string =>
  persona === "academy" ? "/academy/home"
  : persona === "scout" ? "/scout/find"
  : "/player/home";

interface ApplyPersonaExtras {
  date_of_birth?: string | null;
  parental_consent_status?: "not_required" | "pending" | "approved" | "rejected";
  parent_email?: string | null;
  parent_name?: string | null;
  parental_consent_token?: string | null;
}

const applyPersonaToUser = async (
  userId: string,
  userEmail: string | null,
  persona: PersonaValue,
  extras?: ApplyPersonaExtras
): Promise<void> => {
  // Surface upsert failures to Sentry so a silent RLS denial doesn't
  // pretend the persona was applied. If profile.role never makes it
  // to the DB, the next `refreshProfile()` reads back a roleless
  // profile, the ghost-session branch in resolveDestinationForUser
  // fires, and the user is bounced to /auth — looking like a loop.
  // We don't toast here because this runs on the redirect path after
  // OAuth / email confirmation; the caller already knows to retry
  // signup if the destination resolution fails.
  const { error: profileErr } = await supabase.from("profiles").upsert(
    {
      id: userId,
      role: persona,
      email: userEmail,
      ...(extras ?? {}),
    },
    { onConflict: "id" }
  );
  if (profileErr) {
    // eslint-disable-next-line no-console
    console.error("[auth] applyPersonaToUser → profile upsert failed", profileErr);
    reportError(profileErr, {
      feature: "apply_persona",
      callsite: "AuthScreen.applyPersonaToUser.profile",
      user_id: userId,
      persona,
    });
  }
  if (persona === "academy") {
    const { error: academyErr } = await supabase.from("academies").upsert(
      { user_id: userId, email: userEmail },
      { onConflict: "user_id", ignoreDuplicates: true }
    );
    if (academyErr) {
      // eslint-disable-next-line no-console
      console.error("[auth] applyPersonaToUser → academy upsert failed", academyErr);
      reportError(academyErr, {
        feature: "apply_persona",
        callsite: "AuthScreen.applyPersonaToUser.academy",
        user_id: userId,
      });
    }
  }
};

/**
 * Resolve where a signed-in user belongs and push them there.
 *
 * Order of checks:
 *  1. If a pending persona was stashed pre-OAuth/pre-confirm AND the user
 *     has no role yet, apply it and head to that role's onboarding.
 *  2. Otherwise read profiles.role and route to onboarding or home.
 *  3. Academy users have onboarding state on the academies row, not on
 *     profiles, so look that up separately.
 *
 * Web admins (VITE_ADMIN_USER_IDS) are the one exception to the web
 * build being academy-only: after the ghost-session check they go to
 * `adminReturnTo` (the /admin/* page RequireAuth bounced them from) if
 * set, and a player/scout admin with no return path goes to the admin
 * home rather than being signed out. This is LOGIN routing only — signup
 * never reaches here with an admin id, and the web signup persona is
 * still forced to academy.
 *
 * `refreshProfile` is invoked after we apply a pending persona so the
 * AuthContext picks up the new profile.role (and the freshly-created
 * academies row) before the destination screen mounts. Without this the
 * destination renders against stale auth state from the auto-fired
 * onAuthStateChange that ran before the role was written.
 */
async function resolveDestinationForUser(
  userId: string,
  userEmail: string | null,
  refreshProfile: () => Promise<void>,
  adminReturnTo: string | null = null
): Promise<string> {
  const pending = consumePendingSignup();

  const { data, error } = await supabase
    .from("profiles")
    .select("role, onboarding_complete")
    .eq("id", userId)
    .maybeSingle();

  if (!error && pending && (!data || !data.role)) {
    const extras: ApplyPersonaExtras | undefined = pending.persona === "player" && pending.dob
      ? {
          date_of_birth: pending.dob,
          parental_consent_status: pending.parentEmail ? "pending" : "not_required",
          parent_email: pending.parentEmail ?? null,
          parent_name: pending.parentName ?? null,
          parental_consent_token: pending.consentToken ?? null,
        }
      : undefined;
    await applyPersonaToUser(userId, userEmail, pending.persona, extras);
    if (pending.persona === "player" && pending.parentEmail && pending.consentToken) {
      // Don't block onboarding on send failure (user can resend from
      // the banner), but DO capture failures to Sentry so we can spot
      // Resend / EF outages quickly. Errors are wrapped because this
      // path runs during the redirect handler and shouldn't throw.
      try {
        const r = await sendParentalConsentEmail({
          playerEmail: userEmail ?? "",
          parentEmail: pending.parentEmail,
          parentName: pending.parentName ?? "",
          consentToken: pending.consentToken,
        });
        if (!r.ok) {
          reportError(new Error(r.error ?? "Consent email send failed"), {
            feature: "parental_consent_email",
            callsite: "AuthScreen.resolveDestinationForUser",
            user_id: userId,
          });
        }
      } catch (err) {
        reportError(err, {
          feature: "parental_consent_email",
          callsite: "AuthScreen.resolveDestinationForUser",
          user_id: userId,
        });
      }
    }
    await refreshProfile();
    return onboardingPathFor(pending.persona);
  }

  // Ghost session — caller is authenticated but their profile has no role
  // and there's no pending-signup blob to recover from (the legitimate
  // post-signup path above already returned). Could be: profile row
  // missing, signup that failed mid-applyPersonaToUser, or a transient
  // RLS / network hiccup on the lookup above. The historical behaviour
  // was to route to /role-select which silently re-attributed an
  // existing account to a fresh persona — wrong for existing users.
  // Sign them out so the next mount starts clean, then send them to the
  // login screen.
  if (error || !data || !data.role) {
    await supabase.auth.signOut();
    return "/auth";
  }

  // Web admin login, any role. Must stay above the player/scout web
  // sign-out below. An academy admin with no return path falls through to
  // its normal academy routing.
  if (isWebPlatform() && isAdminUserId(userId)) {
    if (adminReturnTo) return adminReturnTo;
    if (data.role === "player" || data.role === "scout") return ADMIN_HOME_PATH;
  }

  if (data.role === "academy") {
    const { data: acad } = await supabase
      .from("academies")
      .select("onboarding_completed")
      .eq("user_id", userId)
      .maybeSingle();
    return acad?.onboarding_completed ? "/academy/home" : "/academy/onboarding";
  }

  // Parent role doesn't flow through the email/password signup, so we
  // never need to apply a pending persona here. Route returning parents
  // to the portal landing screen (or the onboarding flow if they have
  // no verified child links yet — covered in routeParentForward).
  if (data.role === "parent") {
    return isWebPlatform() ? "/parent" : "/parent/home";
  }

  // Web build is academy-only — every /player/* and /scout/* path is
  // wrapped in <PlatformGuard allow="native" /> which redirects back
  // to /auth on web. If we return the player/scout home path for a
  // user signing in on web, the navigate() lands on the gated route,
  // the guard bounces them straight back here, AuthScreen remounts,
  // the redirect effect re-fires for the same user.id, and we loop
  // until Supabase rate-limits the auth calls (~50 iterations). The
  // user sees the splash + auth screen flicker repeatedly with no
  // visible error in the console.
  //
  // Detect the case explicitly: sign the session out so the next
  // mount renders the auth form (not the redirect path), and surface
  // a toast so the user understands their existing player/scout
  // account isn't usable on this surface. They can sign up as a new
  // academy from the same screen, or sign in via the mobile app
  // where their existing role is valid.
  if ((data.role === "player" || data.role === "scout") && isWebPlatform()) {
    await supabase.auth.signOut();
    // Title matches the product copy verbatim ("Unauthorized User —
    // Only Academy Access"); the description gives the actionable
    // hint so a Player or Scout knows where to actually sign in.
    toast.error("Unauthorized User — Only Academy Access", {
      description:
        `This account is registered as a ${data.role}. Please sign in through the SportsBio mobile app to access your ${data.role} profile.`,
      duration: 8000,
    });
    return "/auth";
  }

  if (!data.onboarding_complete) return onboardingPathFor(data.role);
  return homePathFor(data.role);
}

const AuthScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Set only when RequireAuth bounced us here from an /admin/* page.
  const adminReturnTo = adminReturnPath(location.state);
  const { user, loading: authLoading, sessionAuthenticated, refreshProfile } = useAuth();
  // Sign In is the default — most opens are returning users.
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // On web, force persona = "academy". The web build is the Academy-only
  // surface; player/scout signup is mobile-only. The persona toggle UI is
  // also hidden below to make this irreversible from the UI side.
  const [persona, setPersona] = useState<PersonaValue>(
    isWebPlatform() ? "academy" : "player",
  );
  const [dob, setDob] = useState("");                  // YYYY-MM-DD, player signup only
  const [parentEmail, setParentEmail] = useState("");  // 13–17 player signup only
  const [parentName, setParentName] = useState("");
  const [loading, setLoading] = useState(false);
  // Cloudflare Turnstile state. Token is single-use — clear it whenever
  // the widget unmounts/re-renders (persona change, login toggle, signup
  // failure) so a stale token can never be replayed.
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  // Computed once per dob change. null when dob isn't a valid date.
  const age = useMemo(() => computeAge(dob), [dob]);
  const isPlayerSignup = !isLogin && persona === "player";
  const requiresParentInfo = isPlayerSignup && age !== null && age >= MIN_PLAYER_AGE && age < MINOR_AGE_CEILING;
  const blockedUnderAge = isPlayerSignup && age !== null && age < MIN_PLAYER_AGE;
  // CAPTCHA is shown only on Player + Scout email signup. Academy bypasses
  // by spec; login and forgot-password screens never see it. Also skipped
  // when the site key isn't configured (dev environment).
  //
  // TURNSTILE_ENABLED gate (see file-header block): when false, this
  // resolves to false universally — widget is not rendered, canSubmit
  // doesn't gate, the pre-signup verifyCaptchaToken() never runs, and
  // the on-error widget reset is a no-op (null ref). Flipping the
  // flag back to true restores the full flow with no other edits.
  const showCaptcha =
    TURNSTILE_ENABLED &&
    !isLogin && (persona === "player" || persona === "scout") && !!TURNSTILE_SITE_KEY;

  // Any change that hides or remounts the widget invalidates the token —
  // Cloudflare tokens are tied to a specific widget instance and
  // single-use.
  useEffect(() => {
    setCaptchaToken(null);
    turnstileRef.current?.reset();
  }, [persona, isLogin]);

  // If the user is already authenticated when they land on /auth
  // (e.g. they hit back, or reopened the app, or just completed Google
  // OAuth), bounce them forward. Runs only once the initial session
  // probe finishes.
  //
  // Dedupe per user.id using a MODULE-LEVEL variable (see top-of-file
  // comment on lastResolvedDestForUserId for the why). Two things to
  // know:
  //   • Supabase fires onAuthStateChange repeatedly for the same
  //     account (INITIAL_SESSION, SIGNED_IN, TOKEN_REFRESHED,
  //     USER_UPDATED). Each fire hands us a new `user` object
  //     reference, which would re-fire this effect on every render.
  //   • A guard further down the route tree (PlatformGuard at
  //     /player/* and /scout/* on web) can bounce the user back to
  //     /auth, which unmounts and re-mounts THIS component. A
  //     component-level ref resets on remount, which is why this
  //     dedupe lives at module scope.
  // The !user branch clears the cache so a real sign-out → sign-in
  // cycle (different account, or same account after a hard sign-out)
  // resolves freshly.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      lastResolvedDestForUserId = null;
      lastResolvedDest = null;
      return;
    }
    // User has a persisted Supabase session but hasn't actively
    // signed in within this tab session. Hold the redirect — the
    // render path below shows the auth form so they can sign in.
    // Once they do, the SIGNED_IN handler in AuthContext flips
    // sessionAuthenticated, this effect re-fires, and the redirect
    // runs.
    if (!sessionAuthenticated) return;
    if (lastResolvedDestForUserId === user.id) {
      // Already resolved for this user, but the component has remounted
      // (e.g. a route guard bounced us back to /auth). Re-navigate to the
      // known destination instead of bailing silently — bailing leaves
      // authInFlight true and strands the user on the "Signing you in…"
      // spinner forever.
      if (lastResolvedDest) navigate(lastResolvedDest, { replace: true });
      return;
    }
    lastResolvedDestForUserId = user.id;
    let cancelled = false;
    (async () => {
      const dest = await resolveDestinationForUser(
        user.id,
        user.email ?? null,
        refreshProfile,
        adminReturnTo
      );
      if (!cancelled) { lastResolvedDest = dest; navigate(dest, { replace: true });
    }
    })();
    return () => { cancelled = true; };
  }, [authLoading, user, sessionAuthenticated, navigate, refreshProfile, adminReturnTo]);

  const canSubmit = (() => {
    if (isLogin) return email.trim() !== "" && password.trim() !== "";
    const baseFields = name.trim() !== "" && email.trim() !== "" && password.trim() !== "";
    if (!baseFields) return false;
    if (persona === "player") {
      // Player signup needs a valid (non-blocked) DOB.
      if (age === null) return false;
      if (age < MIN_PLAYER_AGE) return false;
      if (age < MINOR_AGE_CEILING) {
        if (parentEmail.trim() === "" || parentName.trim() === "") return false;
      }
    }
    // CAPTCHA gate — applies whenever the widget is rendered (Player +
    // Scout signup with a configured site key). Academy bypasses by spec.
    if (showCaptcha && !captchaToken) return false;
    return true;
  })();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || loading) return;
    setLoading(true);
    try {
      if (isLogin) {
        // ---- SIGN IN -------------------------------------------------
        const { data, error } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password,
        });
        if (error) throw error;
        const userId = data.user?.id;
        if (!userId) throw new Error("Sign-in succeeded but no user returned");

        const dest = await resolveDestinationForUser(
          userId,
          data.user?.email ?? null,
          refreshProfile,
          adminReturnTo
        );
        // Only greet the user when we're actually proceeding into the
        // app. If resolveDestinationForUser routes them back to /auth
        // (ghost session, or — most commonly — a player/scout signing
        // in on the web build), it has already shown its own toast
        // explaining what happened, so we don't pile a contradictory
        // "Welcome back" on top of it.
        if (dest !== "/auth") {
          toast.success("Welcome back");
        }
        navigate(dest, { replace: true });
      } else {
        // ---- SIGN UP -------------------------------------------------
        // Player, Scout and Academy can all sign up on every platform now
        // that the Academy app experience has shipped.

        // Hard age gate first — never create an auth.users row for under-13.
        if (persona === "player") {
          if (age === null) {
            toast.error("Please enter your date of birth");
            return;
          }
          if (age < MIN_PLAYER_AGE) {
            toast.error(
              `SportsBio requires users to be ${MIN_PLAYER_AGE} or older. ` +
              "If you are a player under 13, please ask your academy to add you to their roster."
            );
            return;
          }
        }

        // Per-device rate limit on account creation.
        if (!canAttemptSignup()) {
          toast.error("Too many signup attempts on this device. Please try again later.");
          return;
        }

        // Server-verify the Cloudflare Turnstile token before creating any
        // auth.users row. Only runs when the widget is shown (Player +
        // Scout signup with a configured site key); Academy bypasses by
        // spec, and dev environments without the site key skip too. On
        // verify failure we reset the widget so the user can re-solve
        // — Cloudflare tokens are single-use, so even a "valid but
        // already-consumed" token can't be reused.
        if (showCaptcha) {
          if (!captchaToken) {
            toast.error("Please complete the verification.");
            return;
          }
          const cv = await verifyCaptchaToken(captchaToken);
          if (!cv.ok) {
            toast.error(cv.error ?? "Verification failed. Please try again.");
            turnstileRef.current?.reset();
            setCaptchaToken(null);
            return;
          }
        }

        // Build the player-only extras up-front so we can stash them for the
        // email-confirmation redirect path AND apply them inline on the
        // immediate-session path.
        const isMinorPlayer = persona === "player" && age !== null && age < MINOR_AGE_CEILING;
        const consentToken = isMinorPlayer && typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : undefined;

        const pending: PendingSignupData = {
          persona,
          ...(persona === "player" && dob ? { dob } : {}),
          ...(isMinorPlayer
            ? {
                parentEmail: parentEmail.trim(),
                parentName: parentName.trim(),
                consentToken,
              }
            : {}),
        };
        // Stash BEFORE the network call so the email-confirmation path
        // (where the redirect leaves the page) can pick it up later.
        persistPendingSignup(pending);

        const { data, error } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: {
            data: { name: name.trim() },
            emailRedirectTo: window.location.origin + "/auth",
          },
        });
        recordSignupAttempt();
        if (error) throw error;

        if (data.session && data.user) {
          // Email confirmation disabled — we're signed in immediately.
          // Apply the persona inline. Then refreshProfile() so the
          // AuthContext picks up the role + the academies row BEFORE we
          // navigate; otherwise the onboarding screen renders against the
          // stale state from the auto-fired onAuthStateChange that ran
          // before applyPersonaToUser wrote anything.
          const extras: ApplyPersonaExtras | undefined = persona === "player"
            ? {
                date_of_birth: dob || null,
                parental_consent_status: isMinorPlayer ? "pending" : "not_required",
                parent_email: isMinorPlayer ? parentEmail.trim() : null,
                parent_name: isMinorPlayer ? parentName.trim() : null,
                parental_consent_token: consentToken ?? null,
              }
            : undefined;
          await applyPersonaToUser(data.user.id, data.user.email ?? null, persona, extras);
          consumePendingSignup(); // clear since we already applied it
          if (isMinorPlayer && consentToken) {
            // Await so we can capture send failures to Sentry. Failure
            // doesn't block signup (the user can resend from the banner
            // on PlayerHome), but the toast at the end of this branch
            // shouldn't claim "we've emailed your parent" when we
            // actually couldn't.
            const r = await sendParentalConsentEmail({
              playerEmail: data.user.email ?? email.trim(),
              parentEmail: parentEmail.trim(),
              parentName: parentName.trim(),
              consentToken,
            });
            if (!r.ok) {
              reportError(new Error(r.error ?? "Consent email send failed"), {
                feature: "parental_consent_email",
                callsite: "AuthScreen.handleSubmit",
                user_id: data.user.id,
              });
            }
          }
          await refreshProfile();
          track({
            name: "user_signed_up",
            props: {
              persona,
              signup_method: "email",
              age_bucket: persona === "player" ? ageToBucket(age) : "unknown",
            },
          });
          // GA4 / Firebase persona-specific signup events.
          if (persona === "academy") analytics.academy.signUp();
          else if (persona === "scout") analytics.scout.signUp();
          else if (persona === "player") analytics.player.signUp();
          if (isMinorPlayer) track({ name: "parental_consent_requested" });
          toast.success(
            isMinorPlayer
              ? "Account created — we've emailed your parent for consent"
              : "Account created"
          );
          navigate(onboardingPathFor(persona), { replace: true });
        } else {
          // Email confirmation required. The pending blob stays in
          // localStorage; it'll be applied when the user lands back on /auth
          // after confirming.
          toast.success("Check your email to confirm your account, then sign in");
          setIsLogin(true);
          setPassword("");
        }
      }
    } catch (err) {
      // Detect "user already registered" specifically so the toast
      // tells the user to sign in instead of leaking the raw Supabase
      // error string. Supabase Auth surfaces this as either a 422
      // status with a message containing "registered", or a message
      // containing "already" — we match on both for cross-version
      // robustness. Only relevant on signup (login flow's own errors
      // have different shapes; we route them through getErrorMessage).
      const message = err instanceof Error ? err.message : "";
      const status =
        err && typeof err === "object" && "status" in err
          ? (err as { status?: number }).status
          : undefined;
      const isDuplicate =
        !isLogin &&
        (status === 422 ||
          /already (registered|exists)/i.test(message) ||
          /user.*already/i.test(message));
      // Supabase deliberately returns a single "Invalid login credentials"
      // error for both wrong-password AND user-not-found (prevents email
      // enumeration). For a friendlier UX on the sign-in tab, intercept
      // that specific message and surface a Sign Up action — covers the
      // common case of a returning user typing the wrong email.
      const isInvalidLogin =
        isLogin && /invalid login credentials/i.test(message);
      if (isDuplicate) {
        toast.error("This email is already registered. Try signing in instead.", {
          action: {
            label: "Sign In",
            onClick: () => {
              setIsLogin(true);
              setPassword("");
            },
          },
        });
      } else if (isInvalidLogin) {
        toast.error(
          "User doesn't exist or incorrect password. Sign up first or try again.",
          {
            duration: 5000,
            action: {
              label: "Sign Up",
              onClick: () => {
                setIsLogin(false);
                setPassword("");
              },
            },
          },
        );
      } else {
        toast.error(getErrorMessage(err));
      }
      // If signup failed AFTER CAPTCHA passed (e.g. Supabase rejected the
      // email, network blipped), reset Turnstile so the user can re-solve.
      // The token we sent to verify-captcha is already consumed — leaving
      // it in state would make the next attempt fail with
      // timeout-or-duplicate.
      if (!isLogin && showCaptcha) {
        turnstileRef.current?.reset();
        setCaptchaToken(null);
      }
    } finally {
      // Always clear the spinner so the user is never stuck.
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (loading) return;
    // Persist a fallback pending-persona blob on EVERY Google sign-in —
    // signup or sign-in. resolveDestinationForUser only applies the
    // blob when the post-auth profile has no role yet (i.e. a brand-
    // new account), so existing users hitting Google via the sign-in
    // tab still flow through their existing profile.role normally.
    //
    // Without this, a new user who clicks Google on the SIGN-IN tab
    // (their muscle memory after coming back to the app) lands with
    // a fresh auth.users row but no profile row, hits the ghost-
    // session branch, and gets silently signed out. With the blob
    // present, the same flow seeds a profile and routes them to
    // onboarding — same outcome as if they'd clicked Sign Up first.
    //
    // Persona selection:
    //   • Sign-up tab → use whatever the user picked in the toggle.
    //   • Sign-in tab → default by platform: web is academy-only, so
    //     a new user there is necessarily an academy; on native we
    //     default to "player" (the most common persona; if they're
    //     actually a scout they re-pick during onboarding via the
    //     existing /role-select recovery flow).
    const persistedPersona: PersonaValue = isLogin
      ? (isWebPlatform() ? "academy" : "player")
      : persona;
    persistPendingSignup({ persona: persistedPersona });
    setLoading(true);
    try {
      // Native (Capacitor Android / iOS): use the NATIVE Google Sign-In
      // sheet via Firebase and hand the resulting ID token straight to
      // Supabase. No system browser, no custom-scheme deep link, no code
      // exchange — which removes every step where the old flow could
      // strand the user on an infinite "Signing you in…" spinner.
      //
      // Web: keep the default in-page redirect flow — the browser
      // handles it natively and Supabase's detectSessionInUrl auto-
      // exchanges the code on landing back at /auth.
      if (Capacitor.isNativePlatform()) {
        const result = await FirebaseAuthentication.signInWithGoogle({
          useCredentialManager: false
        });
        const idToken = result.credential?.idToken;
        if (!idToken) throw new Error("Google sign-in returned no ID token");

        const { error } = await supabase.auth.signInWithIdToken({
          provider: "google",
          token: idToken,
        });
        if (error) throw error;
        // Session is live immediately. The redirect effect above picks up
        // the new user and navigates; clear the spinner so a cancelled or
        // stalled navigation never leaves the button disabled.
        setLoading(false);
      } else {
        const { error } = await supabase.auth.signInWithOAuth({
          provider: "google",
          options: { redirectTo: window.location.origin + "/auth" },
        });
        if (error) throw error;
        // Browser redirects away; no further navigation needed here.
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? `${err.name}: ${err.message}` : JSON.stringify(err),
        { duration: 15000 }
      );
      setLoading(false);
    }
  };

  // Hide the form when we know a redirect is imminent. Three windows
  // cover the entire "user shouldn't see the form" lifecycle:
  //   1. authLoading — the initial getSession probe is still running.
  //   2. `?code=` in the URL — we just landed back from Google OAuth
  //      and Supabase's detectSessionInUrl is exchanging the code for
  //      a session.
  //   3. user != null AND sessionAuthenticated — the session is live
  //      AND the operator actively signed in this tab session, so the
  //      redirect effect above is about to navigate. The
  //      sessionAuthenticated requirement is crucial: a stale
  //      persisted session (tab was closed and reopened) leaves
  //      user != null but sessionAuthenticated === false, and in that
  //      case we want the FORM to render so the operator re-auths.
  // For fresh visitors with no session, none of these are true and the
  // form renders immediately as before.
  const hasOAuthCode =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("code");
  const authInFlight =
    authLoading || hasOAuthCode || (user != null && sessionAuthenticated);

  if (authInFlight) {
    return (
      <div className="page-scroll relative bg-background flex flex-col items-center justify-center px-6 py-12">
        <div className="absolute w-80 h-80 rounded-full bg-primary/5 blur-[100px] -top-20 pointer-events-none" />
        <div className="flex flex-col items-center gap-4 z-10">
          <div className="flex items-center gap-3">
            <SportsBioIcon size={44} />
            <h1 className="font-heading text-2xl font-bold">
              <span className="text-gradient-green font-extrabold">Sports</span>Bio
            </h1>
          </div>
          <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin mt-2" />
          <p className="text-sm text-muted-foreground">Signing you in…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-scroll relative bg-background flex flex-col items-center justify-center px-6 py-12">
      <div className="absolute w-80 h-80 rounded-full bg-primary/5 blur-[100px] -top-20 pointer-events-none" />
      <div className="absolute w-60 h-60 rounded-full bg-accent/5 blur-[100px] bottom-10 right-10 pointer-events-none" />

      <motion.div
        className="flex items-center gap-3 mb-2 z-10"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <SportsBioIcon size={44} />
        <h1 className="font-heading text-2xl font-bold">
          <span className="text-gradient-green font-extrabold">Sports</span>Bio
        </h1>
      </motion.div>

      <motion.p
        className="text-muted-foreground mb-6 z-10 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        {isLogin
          ? "Welcome back"
          : isWebPlatform()
            ? "Create your academy account"
            : "Create your account to get started"}
      </motion.p>

      <motion.div
        className="w-full max-w-sm z-10"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        {/* Persona toggle — only shown on signup. Sign-in users already
            have a role on their profile, so this is unnecessary there.
            Player / Scout / Academy are all selectable on native now that
            the Academy app experience has shipped. (The web build is
            Academy-only and doesn't render this toggle.) */}
        {!isLogin && !isWebPlatform() && (
          <div
            className="flex p-1 rounded-xl border mb-5"
            style={{ background: "#1C2235", borderColor: "rgba(123, 104, 238, 0.3)" }}
            role="radiogroup"
            aria-label="Account type"
          >
            {PERSONA_OPTIONS.map((opt) => {
              const selected = persona === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setPersona(opt.value)}
                  className={`relative flex-1 rounded-[10px] py-2.5 text-xs font-semibold transition-colors ${
                    selected ? "text-white shadow-lg" : "text-[#6B7A99] hover:text-foreground"
                  }`}
                  style={{
                    background: selected
                      ? "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--accent)))"
                      : "transparent",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Google Sign In */}
        <button
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl border-2 border-border/30 bg-card/30 text-foreground font-medium text-sm hover:border-border/60 transition-all mb-6 disabled:opacity-50"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 h-px bg-border/30" />
          <span className="text-xs text-muted-foreground">or</span>
          <div className="flex-1 h-px bg-border/30" />
        </div>

        {/* Email form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/50" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
                autoComplete="name"
                className="w-full rounded-xl px-4 py-3.5 pl-11 text-foreground placeholder:text-muted-foreground/30 focus:outline-none focus:ring-2 focus:ring-primary/40 border border-border/30 bg-card/30 text-sm"
              />
            </div>
          )}

          {isPlayerSignup && (
            <div>
              <div className="relative">
                <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/50 pointer-events-none" />
                <input
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  max={new Date().toISOString().slice(0, 10)}
                  aria-label="Date of birth"
                  className="w-full rounded-xl px-4 py-3.5 pl-11 text-foreground placeholder:text-muted-foreground/30 focus:outline-none focus:ring-2 focus:ring-primary/40 border border-border/30 bg-card/30 text-sm"
                />
              </div>
              <p className="text-[11px] text-muted-foreground/70 mt-1.5 px-1">
                We use your date of birth to verify age and enable parental consent for under-18s.
              </p>
              {blockedUnderAge && (
                <p className="text-[11px] text-destructive mt-2 px-1 leading-snug">
                  SportsBio requires users to be {MIN_PLAYER_AGE} or older. If you are a player under {MIN_PLAYER_AGE},
                  please ask your academy to add you to their roster.
                </p>
              )}
            </div>
          )}

          {requiresParentInfo && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-3">
              <div className="flex items-start gap-2">
                <Users className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                <p className="text-[11px] text-amber-200/90 leading-snug">
                  You're under 18 — we'll email your parent or guardian for consent
                  before scouts can find your profile.
                </p>
              </div>
              <input
                type="text"
                value={parentName}
                onChange={(e) => setParentName(e.target.value)}
                placeholder="Parent / guardian full name"
                className="w-full rounded-lg px-3 py-2.5 text-foreground placeholder:text-muted-foreground/30 focus:outline-none focus:ring-2 focus:ring-amber-500/40 border border-amber-500/20 bg-card/30 text-sm"
              />
              <input
                type="email"
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value)}
                placeholder="Parent / guardian email"
                autoComplete="off"
                className="w-full rounded-lg px-3 py-2.5 text-foreground placeholder:text-muted-foreground/30 focus:outline-none focus:ring-2 focus:ring-amber-500/40 border border-amber-500/20 bg-card/30 text-sm"
              />
            </div>
          )}

          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/50" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              autoComplete="email"
              className="w-full rounded-xl px-4 py-3.5 pl-11 text-foreground placeholder:text-muted-foreground/30 focus:outline-none focus:ring-2 focus:ring-primary/40 border border-border/30 bg-card/30 text-sm"
            />
          </div>

          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/50" />
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoComplete={isLogin ? "current-password" : "new-password"}
              minLength={6}
              className="w-full rounded-xl px-4 py-3.5 pl-11 pr-11 text-foreground placeholder:text-muted-foreground/30 focus:outline-none focus:ring-2 focus:ring-primary/40 border border-border/30 bg-card/30 text-sm"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground/50 hover:text-muted-foreground"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {isLogin && (
            <div className="flex justify-end -mt-1">
              <Link
                to="/auth/forgot-password"
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Forgot password?
              </Link>
            </div>
          )}

          {showCaptcha && (
            <div className="flex justify-center pt-1">
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY as string}
                options={{ theme: "dark", size: "flexible" }}
                onSuccess={(token) => setCaptchaToken(token)}
                onError={() => setCaptchaToken(null)}
                onExpire={() => setCaptchaToken(null)}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit || loading}
            className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-heading font-bold text-sm transition-all ${
              canSubmit && !loading
                ? "gradient-green text-white shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:scale-[1.01]"
                : "bg-muted/20 text-muted-foreground/40 cursor-not-allowed"
            }`}
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                {isLogin ? "Sign In" : "Create Account"}
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>

        <p className="text-center text-sm text-muted-foreground mt-6">
          {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
          <button
            onClick={() => setIsLogin(!isLogin)}
            className="text-primary font-medium hover:underline"
          >
            {isLogin ? "Sign Up" : "Sign In"}
          </button>
        </p>
      </motion.div>
    </div>
  );
};

export default AuthScreen;
